import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { brandInfo } from '../data/products';

export const OrderConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderNumber, orderDetails } = location.state || {};

  useEffect(() => {
    if (!orderNumber) {
      navigate('/');
    }
  }, [orderNumber, navigate]);

  if (!orderNumber) {
    return null;
  }

  const estimatedDelivery = new Date();
  estimatedDelivery.setDate(estimatedDelivery.getDate() + 10);

  return (
    <main className="pt-32 pb-24 bg-[#FAF8F5]" data-testid="order-confirmation-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <div className="w-20 h-20 mx-auto bg-[#10B981]/10 rounded-full flex items-center justify-center mb-6">
            <CheckCircle className="w-10 h-10 text-[#10B981]" />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-[#2D4A3E] mb-4" data-testid="confirmation-title">
            Order Placed Successfully!
          </h1>
          <p className="font-['Montserrat'] text-base text-[#5A6B5D]">
            Thank you for your order. We've received your request and will process it shortly.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white border border-[#E8E4DE] p-6 md:p-8 mb-6"
          data-testid="order-details"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-[#E8E4DE] pb-4 mb-6 gap-4">
            <div>
              <p className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">Order Number</p>
              <p className="font-['Cormorant_Garamond'] text-2xl text-[#2D4A3E]" data-testid="order-number">
                {orderNumber}
              </p>
            </div>
            <div className="sm:text-right">
              <p className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">Order Date</p>
              <p className="font-['Montserrat'] text-[#2D4A3E]">
                {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>

          {/* Order Items */}
          <div className="mb-6">
            <h3 className="font-['Montserrat'] font-semibold text-xs uppercase tracking-wider text-[#8B7355] mb-4">
              Order Items
            </h3>
            <div className="space-y-4">
              {orderDetails?.items?.map(item => (
                <div key={item.id} className="flex gap-4">
                  <div className="w-14 h-14 bg-[#FAF8F5] flex-shrink-0 border border-[#E8E4DE]">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-['Montserrat'] text-sm text-[#2D4A3E]">{item.name}</h4>
                    <p className="font-['Montserrat'] text-xs text-[#8B7355]">Qty: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-['Montserrat'] font-semibold text-[#2D4A3E]">
                      ${(item.price * item.quantity).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="border-t border-[#E8E4DE] pt-4 space-y-2 font-['Montserrat']">
            <div className="flex justify-between text-sm text-[#5A6B5D]">
              <span>Subtotal</span>
              <span>${orderDetails?.subtotal?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm text-[#5A6B5D]">
              <span>Shipping</span>
              <span className={orderDetails?.shipping === 0 ? 'text-[#10B981] font-semibold' : ''}>
                {orderDetails?.shipping === 0 ? 'FREE' : `$${orderDetails?.shipping}`}
              </span>
            </div>
            <div className="flex justify-between text-lg font-semibold text-[#2D4A3E] pt-2 border-t border-[#E8E4DE]">
              <span>Total</span>
              <span data-testid="order-total">${orderDetails?.total?.toLocaleString()}</span>
            </div>
          </div>
        </motion.div>

        {/* Shipping & Delivery Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6"
        >
          <div className="bg-white border border-[#E8E4DE] p-5" data-testid="shipping-address">
            <h3 className="font-['Montserrat'] font-semibold text-xs uppercase tracking-wider text-[#8B7355] mb-4 flex items-center gap-2">
              <MapPin size={14} className="text-[#C67D4E]" />
              Shipping Address
            </h3>
            <div className="font-['Montserrat'] text-sm text-[#2D4A3E] space-y-1">
              <p>{orderDetails?.firstName} {orderDetails?.lastName}</p>
              <p>{orderDetails?.address}</p>
              <p>{orderDetails?.city}, {orderDetails?.state} {orderDetails?.zipCode}</p>
              <p>{orderDetails?.phone}</p>
            </div>
          </div>

          <div className="bg-white border border-[#E8E4DE] p-5" data-testid="delivery-info">
            <h3 className="font-['Montserrat'] font-semibold text-xs uppercase tracking-wider text-[#8B7355] mb-4 flex items-center gap-2">
              <Package size={14} className="text-[#C67D4E]" />
              Estimated Delivery
            </h3>
            <p className="font-['Cormorant_Garamond'] text-xl text-[#2D4A3E]">
              {estimatedDelivery.toLocaleDateString('en-US', { 
                weekday: 'long',
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
            <p className="font-['Montserrat'] text-xs text-[#8B7355] mt-2">
              Standard Delivery (7-10 business days)
            </p>
          </div>
        </motion.div>

        {/* Customer Support */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-[#2D4A3E] p-6 md:p-8 text-center"
          data-testid="customer-support"
        >
          <h3 className="font-['Cormorant_Garamond'] text-2xl text-white mb-3">
            Need Help With Your Order?
          </h3>
          <p className="font-['Montserrat'] text-sm text-[#A8B5AB] mb-5">
            Our customer support team is here to assist you
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
            <a 
              href={`mailto:${brandInfo.email}`}
              className="flex items-center justify-center gap-2 text-[#C67D4E] font-['Montserrat'] text-sm"
            >
              <Mail size={16} />
              {brandInfo.email}
            </a>
            <a 
              href={`tel:${brandInfo.phone}`}
              className="flex items-center justify-center gap-2 text-[#C67D4E] font-['Montserrat'] text-sm"
            >
              <Phone size={16} />
              {brandInfo.phone}
            </a>
          </div>
        </motion.div>

        {/* Continue Shopping */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-10"
        >
          <Link to="/category/all">
            <Button 
              className="bg-[#2D4A3E] text-white hover:bg-[#1E332A] px-8 py-6 rounded-sm text-xs uppercase tracking-wider font-semibold font-['Montserrat']"
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
            </Button>
          </Link>
        </motion.div>
      </div>
    </main>
  );
};
